import { GridColDef, GridRowSelectionModel } from "@mui/x-data-grid";
import { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DisplayAPIsException from "../../../../BasicComponents/DisplayAPIsExceptionAlert";
import { logEvent } from "../../../../helper/AppInsightsHelper";
import { CustomShimmer } from "../../../../helper/CustomShimmer";
import { formatDate } from "../../../../helper/DateFormat";
import { ProductViewFiltersContext } from "../../../../pages/ProductView";
import { DATA_GRID_CONFIG } from "../../../../SharedComponents/Constants/CustomCustomerList.constants";
import CustomCard from "../../../../SharedComponents/CustomCard";
import { CustomDataGrid } from "../../../../SharedComponents/CustomDataGrid/CustomDataGrid";
import { getSupportSelector } from "../../../../slices/ExploreProducts.slice";
import { useAppSelector } from "../../../../store/hooks";
import { handleToggleDataGridRowSelection } from "../../../../Utils/js-common";
import { SupportTableAIInsightsPanel } from "../../../CustomerView/SupportTableAIInsightsPanel";
import { PRODUCT_VIEW_DATA } from "../../Common/ProductView.constants";

const OpenCasesTab = () => {
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [paginationOptions, setPaginationOptions] = useState(DATA_GRID_CONFIG.pagination);
  const handleSelection = handleToggleDataGridRowSelection(selectionModel, setSelectionModel);
  const showNoData = () => {
    const rootStyle = {
      alignItems: "center",
      display: "flex",
      fontSize: 18,
      fontWeight: 600,
      justifyContent: "center",
      padding: 200
    };
    return <div style={rootStyle}>{"Support details are not available for this Product."} </div>;
  };
  const [searchParams] = useSearchParams();
  const filters = useContext(ProductViewFiltersContext);
  const productName: any = searchParams.get("productname") ?? PRODUCT_VIEW_DATA.PRODUCT_VIEW.DEFAULT_PRODUCT_NAME;

  const columns: GridColDef[] = [
    {
      disableColumnMenu: true,
      field: "caseUri",
      filterable: false,
      headerName: "AI Summary",
      sortable: false,
      width: 100,
      renderCell: (params) => (
        <SupportTableAIInsightsPanel
          caseNumber={params.row.caseNumber}
          serviceRequestNumber={params.row.serviceRequestNumber}
        />
      )
    },
    {
      headerName: "Request Number",
      field: "serviceRequestNumber",
      width: 180,
      sortable: true,
      renderCell: (params) => (
        <a
          style={{
            color: "rgb(0, 120, 212)",
            textDecoration: "none",
            cursor: "pointer"
          }}
          href={params.row.caseUri}
          target="_blank"
          rel="noopener noreferrer"
          title={params.row.caseUri}
          tabIndex={params.row.caseUri}
        >
          {params.row.serviceRequestNumber}
        </a>
      )
    },
    {
      headerName: "Title",
      field: "title",
      width: 400,
      sortable: true
    },
    {
      headerName: "Product Name",
      field: "productName",
      width: 160,
      sortable: true
    },
    {
      headerName: "Created Time",
      type: "date",
      field: "createdTime",
      width: 120,
      sortable: true,
      valueGetter: (value, row) => new Date(row.createdTime),
      renderCell: (params) => formatDate(params.row.createdTime)
    },
    {
      headerName: "Closed Time",
      type: "date",
      field: "closedDateTime",
      width: 120,
      sortable: true,
      valueGetter: (vaue, row) => new Date(row.closedDateTime),
      renderCell: (params) => formatDate(params.row.closedDateTime)
    },
    {
      headerName: "Service Request State",
      field: "serviceRequestState",
      width: 170,
      sortable: true
    },
    {
      headerName: "CSAT",
      type: "number",
      field: "csat",
      width: 80,
      sortable: true,
      valueGetter: (value, row) => row.csat
    },
    {
      headerName: "TimeInCritsit",
      type: "number",
      field: "timeInCritsit",
      width: 150,
      sortable: true
    },
    {
      headerName: "TimeInCritsit1",
      type: "number",
      field: "timeInCritsit1",
      width: 150,
      sortable: true
    },
    {
      headerName: "TimeInCritsitA",
      type: "number",
      field: "timeInCritsitA",
      width: 150,
      sortable: true
    },
    {
      headerName: "Days To Close",
      type: "number",
      field: "daysToClose",
      width: 150,
      sortable: true
    },
    {
      headerName: "Is IRMet",
      field: "isIRMet",
      width: 150,
      sortable: true
    },
    {
      headerName: "Service Request Current Severity",
      field: "serviceRequestCurrentSeverity",
      width: 240,
      sortable: true
    },
    {
      headerName: "Service Request Max Severity",
      field: "serviceRequestMaxSeverity",
      width: 240,
      sortable: true
    },
    {
      headerName: "Reopen Count",
      type: "number",
      field: "reopenCount",
      width: 150,
      sortable: true
    },
    {
      headerName: "RelatedICM_Id",
      field: "relatedICM_Id",
      width: 150,
      sortable: true
    }
  ];

  useEffect(() => {
    setTimeout(() => {
      const elements = document.getElementsByClassName("MuiDataGrid-virtualScroller");
      Array.from(elements).forEach(function (element) {
        element.setAttribute("tabIndex", "0");
      });
    }, 1000);
  }, []);

  const { data: supportData, isLoading, isSuccess, isError } = useAppSelector((state) => getSupportSelector(state));
  const HandleAPIExcpetion = () => (isError && <DisplayAPIsException height={250} />) || "";

  const customProps = {
    productName: filters?.productName,
    productFilters: JSON.stringify(filters)
  };

  useEffect(() => {
    logEvent("Open Cases Tab - Support", "Product View Page", "OpenCasesTab", customProps);
  }, []);

  return (
    <>
      {isLoading ? (
        <CustomShimmer columnCount={8} />
      ) : (
        (isSuccess && (
          <>
            {supportData?.serviceRequesOpentState.data && supportData?.serviceRequesOpentState?.data.length > 0 ? (
              <CustomCard
                cardHeaderTitle={`Open Cases for - ${productName}`}
                kustoLink={supportData?.serviceRequesOpentState?.kustoLinks?.OpenStateInfo}
                chartTitle={`Product View - Open Cases Table for ${productName}`}
              >
                <div style={{ height: 600, width: "100%" }} tabIndex={0}>
                  <CustomDataGrid
                    columns={columns}
                    getRowId={(row) => row.id}
                    rows={{
                      values: supportData?.serviceRequesOpentState.data ?? [],
                      count: supportData?.serviceRequesOpentState.data.length ?? 0
                    }}
                    slotProps={{ toolbar: { showQuickFilter: true } }}
                    paginationOptions={paginationOptions}
                    setPaginationOptions={setPaginationOptions}
                    onRowSelectionModelChange={handleSelection}
                    rowSelectionModel={selectionModel}
                    initialState={{
                      sorting: {
                        sortModel: [{ field: "createdTime", sort: "desc" }]
                      }
                    }}
                    showExploreDataButtonOnFooter={false}
                  />
                </div>
              </CustomCard>
            ) : (
              showNoData() || ""
            )}
          </>
        )) ||
        ""
      )}
      <HandleAPIExcpetion />
    </>
  );
};

export default OpenCasesTab;
